import { useState, type ReactNode } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Chip, EmptyState, FilterRow, Pane, PaneHead } from '@/components/kit';
import { useShellChrome, useNarrowViewport } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useFlowTemplates } from '@/pages/doctor/shared/useFlows';
import { useCatalog, useFlowItems } from '@/pages/doctor/library/useLibraryApi';
import { FlowEditorPane, FlowsMain, NewFlowModal, executors } from '@/pages/doctor/library/FlowsSection';
import { ItemEditorPane, ItemsMain } from '@/pages/doctor/library/ItemsSection';
import { FlowVoiceProvider, useRoleLabel } from '@/pages/doctor/library/libraryBits';
import type { FlowTemplate } from '@/pages/doctor/shared/useFlows';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { SettingsRail } from './SettingsRail';

/**
 * Clinic manager · Settings ▸ Flows — `g_flows` (f30), `g_flowitems` (f30c)
 * and `g_m_newflow` (f30b).
 *
 * ONE LIST, TWO ENTRANCES. The board says it under the table: "The same editor
 * the doctors reach from their Library". So this screen owns nothing of its
 * own — the list, the docked editor, the Items tab and the New-flow dialog are
 * the Library's components over the Library's endpoints, and an edit here is
 * an edit there. What differs is whose screen it is: the manager is nobody's
 * "you", so the editor is mounted in the clinic voice ("nurse → technician →
 * doctor", "the doctor's READY lane"), and the two boards write their own
 * closing cards.
 *
 * THE TAB AND THE SELECTION LIVE IN THE URL (`?tab=items&item=…`, `?flow=…`),
 * so Back from a flow returns to the same flow, and a link to an item opens it.
 */
export function ManagerFlowsPage() {
  const narrow = useNarrowViewport();
  const go = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: 'flows' | 'items' = params.get('tab') === 'items' ? 'items' : 'flows';
  const [query, setQuery] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  /* Phone only: a tap on a row opens its editor full-screen. */
  const [tapped, setTapped] = useState(false);

  const flows = useFlowTemplates();
  const items = useFlowItems();
  const catalog = useCatalog();

  const flowId = params.get('flow');
  const itemId = params.get('item');
  const flowSelected = flows.data.find((t) => t.id === flowId) ?? flows.data[0] ?? null;
  const itemSelected = items.data.find((i) => i.id === itemId) ?? items.data[0] ?? null;
  /* The editor is keyed by what it edits, and the chrome re-publishes when a
     save changes the row underneath it — otherwise a saved flow's new step
     count would sit stale in the pane head. */
  const flowSig = flows.data.map((t) => `${t.id}:${t.updatedAt}:${t.steps.length}`).join('|');
  const itemSig = items.data.map((i) => `${i.id}:${i.updatedAt}`).join('|');

  const patch = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  };
  const setTab = (t: 'flows' | 'items') => {
    setQuery('');
    patch({ tab: t === 'items' ? 'items' : null });
  };

  usePageMeta({ title: tab === 'items' ? 'Flows ▸ Items' : 'Flows' });

  const pane: ReactNode = tab === 'items'
    ? (itemSelected ? (
      <ItemEditorPane key={itemSelected.id} item={itemSelected} items={items.data} catalog={catalog.data}
        onSaved={(i) => patch({ item: i.id })} />
    ) : (
      <Pane head={<PaneHead overline="Items ▸ editor" title="No item yet" sub="the steps flows are made of" />}>
        <EmptyState
          title={items.isError ? 'The items did not load' : items.isLoading ? 'Loading…' : 'Nothing to edit yet'}
          sub="An item is one link of a chain — who does it, what they read, how long it waits, what it produces."
        />
      </Pane>
    ))
    : (flowSelected ? (
      <FlowEditorPane key={flowSelected.id} template={flowSelected} items={items.data}
        onSaved={(t) => patch({ flow: t.id })} />
    ) : (
      <Pane head={<PaneHead overline="The editor" title="No flow yet" sub="+ New flow writes the first one" />}>
        <EmptyState
          title={flows.isError ? 'The flows did not load' : flows.isLoading ? 'Loading…' : 'Nothing to edit yet'}
          sub="A flow is a chain of steps, each owned by a role — nurse → technician → doctor."
        />
      </Pane>
    ));

  const crumb = usePhoneCrumb('Settings', tab === 'items' ? 'Flows · Items' : 'Flows');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      paneOpen: tapped,
      onPaneClose: () => setTapped(false),
      paneTitle: tab === 'items' ? (itemSelected?.name ?? 'Item') : (flowSelected?.name ?? 'Flow'),
      title: tab === 'items' ? 'Flows ▸ Items' : 'Flows',
      sub: tab === 'items' ? 'the steps flows are made of' : 'the clinic’s chains — executor, timing, dependency',
      search: tab === 'items' ? 'Search an item…' : 'Search a flow, a step…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="flows" />,
      /* Items has no primary on its board — an item is written from the
         Library, where its vocabulary lives; a flow is written from either. */
      primary: tab === 'flows' && !narrow ? { label: '+ New flow', onClick: () => setNewOpen(true) } : undefined,
      /* The pane is rendered by the shell, outside this page's tree, so it
         carries its own voice rather than inheriting the body's. */
      pane: <FlowVoiceProvider clinic>{pane}</FlowVoiceProvider>,
    },
    [tab, tapped, query, flowSelected?.id, itemSelected?.id, flowSig, itemSig, flows.isLoading, items.isLoading,
      flows.isError, items.isError, catalog.data.length, items.data.length, narrow],
  );

  const crumbBtn = {
    cursor: 'pointer', background: 'none', border: 0, padding: 0,
    fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit',
  } as const;

  return (
    <FlowVoiceProvider clinic>
      <div className="maie desk">
        <ManagerSurface />
        {narrow ? null : <FilterRow>
          <p className="t-sec mut">
            <button type="button" className="w6 brand" style={crumbBtn}
              onClick={() => go('/clinic-manager/settings')}>
              ‹ Settings
            </button>
            {tab === 'items' ? (
              <>
                {' '}<span className="mut">▸</span>{' '}
                <button type="button" className="w6 brand" style={crumbBtn} onClick={() => setTab('flows')}>
                  Flows
                </button>
                {' '}<span className="mut">▸</span> <span className="t-sec sec w6">Items</span>
              </>
            ) : (
              <> <span className="mut">▸ Flows</span></>
            )}
          </p>
        </FilterRow>}
        <div className="dcontent">
          {tab === 'items' ? (
            <>
              <FlowsMain templates={flows.data} loading={flows.isLoading} selectedId={null}
                onSelect={() => undefined} onEdit={() => undefined}
                tab={tab} onTab={setTab} query={query} tabsOnly />
              <ItemsMain items={items.data} loading={items.isLoading} failed={items.isError}
                selectedId={itemSelected?.id ?? null}
                onSelect={(id) => { patch({ item: id }); setTapped(true); }} onEdit={(id) => { patch({ item: id }); setTapped(true); }}
                query={query} catalog={catalog.data} split
                note={
                  <Card kind="accent" style={{ marginTop: 12 }}>
                    <p className="t-sec brand">
                      Items are the steps flows are made of. Consent and price ride the ITEM, so a flow
                      inherits them and cannot lose them — and the price keeps living in the order
                      catalog, never here.
                    </p>
                    <p className="t-sec mut mt8">
                      The doctors’ Library and this screen are one list: an item edited here changes
                      every flow that uses it.
                    </p>
                  </Card>
                } />
            </>
          ) : narrow ? (
            <PhoneFlows
              templates={flows.data} query={query}
              loading={flows.isLoading} failed={flows.isError}
              onEdit={(id) => { patch({ flow: id }); setTapped(true); }}
              onTab={setTab} onNew={() => setNewOpen(true)} />
          ) : (
            <FlowsMain templates={flows.data} loading={flows.isLoading} failed={flows.isError}
              selectedId={flowSelected?.id ?? null}
              onSelect={(id) => { patch({ flow: id }); setTapped(true); }} onEdit={(id) => { patch({ flow: id }); setTapped(true); }}
              tab={tab} onTab={setTab} query={query} pageSize={4}
              note={
                <Card kind="accent" style={{ marginTop: 12 }}>
                  <p className="t-sec brand">
                    The same editor the doctors reach from their Library — one list, two entrances.
                    Flows hold choreography, never patient data.
                  </p>
                  <p className="t-sec mut mt8">
                    Pack-seeded at signup · doctors and the clinic manager edit · the executors
                    execute · every edit is logged.
                  </p>
                </Card>
              } />
          )}
        </div>

        <NewFlowModal open={newOpen} items={items.data} onClose={() => setNewOpen(false)}
          onSaved={(t) => { setNewOpen(false); patch({ flow: t.id, tab: null }); }} />
      </div>
    </FlowVoiceProvider>
  );
}

/** Phone `g_t15`: one-line cards, the Flows / Items chips and + New flow under the list. */
function PhoneFlows({ templates, query, loading, failed, onEdit, onTab, onNew }: {
  templates: FlowTemplate[];
  query: string;
  loading: boolean;
  failed: boolean;
  onEdit: (id: string) => void;
  onTab: (t: 'flows' | 'items') => void;
  onNew: () => void;
}) {
  const labels = useRoleLabel();
  const q = query.trim().toLowerCase();
  const rows = q ? templates.filter((t) => `${t.name} ${executors(t.steps, labels)}`.toLowerCase().includes(q)) : templates;
  return (
    <>
      <p className="t-sec mut" style={{ margin: '12px 0' }}>
        Team choreography — the same editor the doctors use in their Library
      </p>
      <div style={{ display: 'grid', gap: 10 }}>
        {rows.length === 0 ? (
          <EmptyState
            title={failed ? 'The flows did not load' : loading ? 'Loading…' : q ? `No flow matches “${query}”` : 'No flows yet'}
            sub="+ New flow writes the first one — a chain of steps, each owned by a role."
          />
        ) : rows.map((t) => (
          <button key={t.id} type="button" className="card card-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%', padding: 14, boxSizing: 'border-box', margin: 0 }}
            onClick={() => onEdit(t.id)}>
            <span className="grow" style={{ minWidth: 0 }}>
              <span className="t-strong" style={{ display: 'block' }}>{t.name}</span>
              <span className="t-cap mut" style={{ display: 'block' }}>
                {executors(t.steps, labels) || '—'} · {t.steps.length} step{t.steps.length === 1 ? '' : 's'}
                {t.usageCount ? ` · used ${t.usageCount}× this month` : ''}
                {t.isActive === false ? ' · hidden' : ''}
              </span>
            </span>
            <Chip kind="soft" small>✎ Edit</Chip>
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, margin: '20px 0 16px' }}>
        <Chip kind="brand" small>Flows</Chip>
        <Chip kind="outline" small onClick={() => onTab('items')}>Items</Chip>
      </div>
      <button type="button" className="btn btn-primary" style={{ borderRadius: 999, minHeight: 52, padding: '0 28px' }} onClick={onNew}>
        + New flow
      </button>
      <p className="t-sec mut" style={{ marginTop: 14, textAlign: 'center' }}>
        opens the doctors’ Library flow editor · flows hold no patient data
      </p>
    </>
  );
}
